/**
 * The format and frame-rate pickers (ADR 0036): PRISM's presets grouped by shape, and the rates the chosen format
 * allows. Shared by the top bar, the start screen, the "Change format…" dialog and the template picker inside PRISM.
 * A size or rate outside the catalogue stays shown as an extra option, so nothing changes silently.
 */
import { ASPECTS, FORMAT_GROUPS, aspectOf, fps, rateAllowed, sameAspect, type Aspect, type FrameRate } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';

type Size = { width: number; height: number };

export const sizeKey = (c: Size): string => `${c.width}x${c.height}`;
export const rateKey = (r: FrameRate): string => `${r.num}/${r.den}`;

export function parseSize(v: string): Size {
  const [width, height] = v.split('x').map(Number);
  return { width: width!, height: height! };
}

export function parseRate(v: string): FrameRate {
  const [num, den] = v.split('/').map(Number);
  return { num: num!, den: den! };
}

/** "29.97 fps". */
export const rateLabel = (r: FrameRate): string => t('top.fps', { fps: Math.round(fps(r) * 100) / 100 });

/** "9:16 · Vertical", or "Custom shape" for a size that is none of PRISM's shapes. */
export function shapeLabel(c: Size): string {
  const a = aspectOf(c);
  const shape = ASPECTS.find((x) => x.aspect === a);
  return shape ? t('format.shapeHint', { aspect: a, shape: t(shape.label) }) : t('format.shape.other');
}

/** Whether a size is one of the presets. */
export const isPreset = (c: Size): boolean => FORMAT_GROUPS.some((g) => g.width === c.width && g.height === c.height);

/**
 * The <option>s of a format select: the presets in one optgroup per shape (values "WxH"), then the current size
 * when it is no preset (value "custom"), then any extra options (e.g. "Custom size…"). `disabled` greys out presets
 * (the shapes a graphic already has a layout for).
 */
export function FormatOptions({ current, extra, disabled }: { current?: Size | undefined; extra?: Array<{ value: string; label: string }>; disabled?: ((size: Size) => boolean) | undefined }) {
  const custom = current && !isPreset(current) ? current : undefined;
  return (
    <>
      {ASPECTS.map(({ aspect, label }) => (
        <optgroup key={aspect} label={t('format.group', { aspect, shape: t(label) })}>
          {FORMAT_GROUPS.filter((g) => g.aspect === aspect).map((g) => (
            <option key={g.id} value={sizeKey(g)} disabled={disabled?.(g) ?? false}>
              {t(g.label)}
            </option>
          ))}
        </optgroup>
      ))}
      {(custom || extra?.length) && (
        <optgroup label={t('format.group.custom')}>
          {custom && <option value="custom">{t('format.customSize', { width: custom.width, height: custom.height })}</option>}
          {extra?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

type Sized = { width: number; height: number };

/**
 * "Layouts" when starting a graphic (ADR 0037): the format select is the primary layout; one checkbox per other shape
 * (its first preset size) adds a layout. Sizes in `value` that are no preset shape (e.g. from PRISM's defaults) are
 * listed too.
 */
export function LayoutChoice({ primary, value, onChange, testId = 'layout-choice' }: { primary: Sized; value: readonly Sized[]; onChange: (next: Sized[]) => void; testId?: string }) {
  const p = aspectOf(primary);
  const same = (a: Sized, b: Sized) => sameAspect(a, b);
  const presets = ASPECTS.filter((x) => x.aspect !== p).map((x) => FORMAT_GROUPS.find((g) => g.aspect === x.aspect)!);
  const extra = value.filter((v) => !same(v, primary) && !presets.some((g) => same(g, v)));
  const options: Sized[] = [...presets.map((g) => ({ width: g.width, height: g.height })), ...extra];
  const checked = (o: Sized) => value.some((v) => same(v, o));
  return (
    <fieldset className="layout-choice" data-testid={testId}>
      <legend className="field-label">{t('layouts.choice')}</legend>
      <span className="badge" data-testid={`${testId}-primary`}>
        {t('layouts.choicePrimary', { shape: shapeLabel(primary) })}
      </span>
      {options.map((o) => {
        const a = aspectOf(o);
        const id = `${o.width}x${o.height}`;
        return (
          <label key={id} className="toggle">
            <input
              type="checkbox"
              checked={checked(o)}
              data-testid={`${testId}-${a === 'other' ? id : a.replace(':', 'x')}`}
              onChange={(e) => onChange(e.target.checked ? [...value.filter((v) => !same(v, o)), o] : value.filter((v) => !same(v, o)))}
            />
            <span>{a === 'other' ? t('format.customSize', { width: o.width, height: o.height }) : shapeLabel(o)}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

/** The <option>s of a rate select: the allowed rates, plus the current one marked "(custom)" when it is not allowed. */
export function RateOptions({ rates, current }: { rates: readonly FrameRate[]; current?: FrameRate | undefined }) {
  const extra = current && !rateAllowed(current, rates) ? current : undefined;
  return (
    <>
      {rates.map((r) => (
        <option key={rateKey(r)} value={rateKey(r)}>
          {rateLabel(r)}
        </option>
      ))}
      {extra && <option value={rateKey(extra)}>{t('format.rateCustom', { fps: Math.round(fps(extra) * 1000) / 1000, ratio: rateKey(extra) })}</option>}
    </>
  );
}

/** The value of a rate select for `rate`: the equal allowed rate's key (60000/2002 shows as 30000/1001). */
export function rateValue(rate: FrameRate, rates: readonly FrameRate[]): string {
  const same = rates.find((r) => r.num * rate.den === rate.num * r.den);
  return rateKey(same ?? rate);
}

/** "Vertical 9:16" etc. for a shape filter. */
export const aspectLabel = (a: Exclude<Aspect, 'other'>): string => t('format.group', { aspect: a, shape: t(ASPECTS.find((x) => x.aspect === a)!.label) });

/** The shape of a canvas as a small badge ("9:16"); nothing for a shape PRISM has no format for. */
export function AspectBadge({ canvas }: { canvas: { width: number; height: number } }) {
  const a = aspectOf(canvas);
  if (a === 'other') return null;
  return (
    <span className="aspect-badge" data-testid="aspect-badge" data-aspect={a} title={shapeLabel(canvas)}>
      {a}
    </span>
  );
}
